/**
 * Tasks Page
 * List and manage tasks with full CRUD functionality
 */

import React, { useState, useEffect } from "react";
import { useDebounce } from "../hooks/useDebounce";
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getTasks, deleteTask } from "../services/tasks.service";
import { supabase } from "../services/supabase";
import {
  QUERY_KEYS,
  DEFAULT_PAGE_SIZE,
  TASK_STATUSES,
} from "../utils/constants";
import { formatDate } from "../utils/formatters";
import { TaskStatus, taskPriorityLabel } from "../types/task.types";
import type { Task } from "../types/task.types";
import type { UUID } from "../types/common.types";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import LoadingSpinner from "../components/common/LoadingSpinner";
import TaskForm from "../components/tasks/TaskForm";

interface Profile {
  id: UUID;
  full_name: string;
}

const Tasks: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">(
    TaskStatus.TODO,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [profiles, setProfiles] = useState<Record<UUID, Profile>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  // Fetch tasks using React Query
  const { data, isLoading, error } = useQuery({
    queryKey: [QUERY_KEYS.TASKS, page, statusFilter, debouncedSearch],
    queryFn: () =>
      getTasks(
        {
          page,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: "due_date",
          sortOrder: "asc",
        },
        {
          status: statusFilter !== "all" ? [statusFilter] : undefined,
          search: debouncedSearch || undefined,
        },
      ),
    // Keep the table (and the search box) mounted while a new search loads; without this the
    // page swaps to a spinner on every keystroke and the search box loses focus.
    placeholderData: keepPreviousData,
  });

  // If the current page emptied (last task on it deleted / filtered away), step back.
  useEffect(() => {
    if (data && page > 1 && data.data.length === 0 && data.total > 0) {
      setPage(Math.max(1, Math.ceil(data.total / DEFAULT_PAGE_SIZE)));
    } else if (data && page > 1 && data.total === 0) {
      setPage(1);
    }
  }, [data, page]);

  // Fetch profiles for assigned users
  React.useEffect(() => {
    const fetchProfiles = async () => {
      if (!data?.data) {
        return;
      }

      const userIds = data.data
        .map((task) => task.assigned_to)
        .filter((id): id is UUID => !!id);

      if (userIds.length === 0) {
        return;
      }

      const { data: profilesData, error } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      if (!error && profilesData) {
        const profilesMap = profilesData.reduce(
          (acc, profile) => {
            acc[profile.id] = profile as Profile;
            return acc;
          },
          {} as Record<UUID, Profile>,
        );
        setProfiles(profilesMap);
      }
    };

    fetchProfiles();
  }, [data]);

  // Delete task mutation
  const deleteMutation = useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TASKS] });
    },
  });

  const handleDelete = async (taskId: UUID) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      setActionError(null);
      try {
        await deleteMutation.mutateAsync(taskId);
      } catch (error) {
        console.error("Error deleting task:", error);
        setActionError(
          (error as { message?: string })?.message ||
            "The task could not be deleted.",
        );
      }
    }
  };

  const handleEdit = (task: Task) => {
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };

  // tasks.priority is an INTEGER 1-5 (higher = more urgent).
  const getPriorityColor = (priority: number) => {
    const colors: Record<number, string> = {
      1: "bg-gray-100 text-gray-600",
      2: "bg-gray-100 text-gray-800",
      3: "bg-blue-100 text-blue-800",
      4: "bg-orange-100 text-orange-800",
      5: "bg-red-100 text-red-800",
    };
    return colors[priority] || "bg-gray-100 text-gray-800";
  };

  const getStatusColor = (status: string) => {
    const statusObj = TASK_STATUSES.find((s) => s.value === status);
    const colorMap: Record<string, string> = {
      gray: "bg-gray-100 text-gray-800",
      blue: "bg-blue-100 text-blue-800",
      yellow: "bg-yellow-100 text-yellow-800",
      green: "bg-green-100 text-green-800",
      red: "bg-red-100 text-red-800",
    };
    return statusObj
      ? colorMap[statusObj.color] || colorMap.gray
      : colorMap.gray;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading tasks..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading tasks. Please try again.</p>
      </div>
    );
  }

  const tasks = data?.data || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Tasks</h1>
        <Button onClick={() => setIsCreateModalOpen(true)}>Create Task</Button>
      </div>

      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as TaskStatus | "all")
          }
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">All Tasks</option>
          {TASK_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search tasks..."
          className="px-4 py-2 border border-gray-300 rounded-lg flex-1"
        />
      </div>

      {actionError && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm"
          data-testid="tasks-action-error"
        >
          {actionError}
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Due Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Assigned To
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tasks.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No tasks found
                </td>
              </tr>
            ) : (
              tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {task.title}
                    </div>
                    {task.description && (
                      <div className="text-sm text-gray-500 truncate max-w-md">
                        {task.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}
                    >
                      {TASK_STATUSES.find((s) => s.value === task.status)
                        ?.label || task.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {task.priority && (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}
                      >
                        {taskPriorityLabel(task.priority)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.due_date ? formatDate(task.due_date) : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {task.assigned_to && profiles[task.assigned_to]
                      ? profiles[task.assigned_to].full_name
                      : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(task)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {(data?.total ?? 0) > DEFAULT_PAGE_SIZE && (
          <div
            className="flex items-center justify-between px-6 py-3 border-t border-gray-200 text-sm text-gray-600"
            data-testid="tasks-pager"
          >
            <span>
              Showing {(page - 1) * DEFAULT_PAGE_SIZE + 1}–
              {Math.min(page * DEFAULT_PAGE_SIZE, data?.total ?? 0)} of{" "}
              {data?.total ?? 0}
            </span>
            <div className="space-x-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!data?.hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Task"
      >
        <TaskForm
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TASKS] });
            setIsCreateModalOpen(false);
          }}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Task"
      >
        {selectedTask && (
          <TaskForm
            task={selectedTask}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TASKS] });
              setIsEditModalOpen(false);
              setSelectedTask(null);
            }}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedTask(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default Tasks;
